import {
  endOfMonth,
  format,
  max,
  monthsToQuarters,
  parseISO,
  startOfMonth,
  startOfQuarter,
  subMonths,
  subQuarters,
} from 'date-fns';

import { getItemFromLocalStorage } from './storage';
import { StorageItems } from '../../shared/constants/app';
import { DateLocales, ISO_DATE_FORMAT } from '../../shared/constants/date';
import { SpecialDateFilterValue } from '../../shared/constants/HeadingFilter';
import { IDateRange } from '../../shared/types/filters';

export const getTimeZone = () => {
  const overriddenTimeZone = getItemFromLocalStorage(
    StorageItems.TIMEZONE,
    'string',
  );
  if (overriddenTimeZone) {
    return overriddenTimeZone;
  }

  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const isSpecialDateFilterValue = (
  value: string | number,
): value is SpecialDateFilterValue => {
  return (
    value === SpecialDateFilterValue.ALL_TIME ||
    value === SpecialDateFilterValue.CUSTOM_DATE
  );
};

/**
 * Calculates a date range spanning the specified number of quarters back from the end date.
 *
 * Used for quarter-based filtering in organizational dashboards where users need to view
 * data across multiple complete quarters (e.g., "Last 2 Quarters").
 *
 * @param numberOfQuarters - How many quarters to go back from the end date
 * @param endDate - The end boundary for the date range (typically today or data cutoff)
 * @returns Date range from the start of the earliest quarter to the end date
 *
 * @example
 * // Get last 2 quarters from Jan 15, 2025
 * getQuarterDateRange(2, new Date('2025-01-15'))
 * // Returns: { fromDate: '2024-07-01', toDate: '2025-01-15' }
 */
function getQuarterDateRange(
  numberOfQuarters: number,
  endDate: Date,
): IDateRange {
  const fromDate = startOfQuarter(subQuarters(endDate, numberOfQuarters - 1));

  return {
    fromDate: format(fromDate, ISO_DATE_FORMAT),
    toDate: format(endDate, ISO_DATE_FORMAT),
  };
}

/**
 * Calculates a date range for month-based filtering.
 *
 * Creates a range from the start of the month X months ago to the end of last month.
 * This excludes the current month to ensure only complete months are included in reports.
 *
 * @param monthOffset - Number of months to go back from the end date
 * @param endDate - The reference date to calculate backwards from
 * @returns Date range from start of offset month to end of previous month
 *
 * @example
 * // With endDate = March 15, 2025 and monthOffset = 3
 * getMonthDateRange(3, new Date('2025-03-15'))
 * // Returns: { fromDate: '2024-12-01', toDate: '2025-02-28' }
 * // (Dec 2024 start to Feb 2025 end - excludes current March)
 */
function getMonthDateRange(monthOffset: number, endDate: Date): IDateRange {
  return {
    fromDate: format(
      startOfMonth(subMonths(endDate, monthOffset)),
      ISO_DATE_FORMAT,
    ),
    toDate: format(endOfMonth(subMonths(endDate, 1)), ISO_DATE_FORMAT),
  };
}

/**
 * Handles the special "ALL_TIME" filter that shows data from the beginning of the user's tenure.
 *
 * If a contract date exists, uses that as the start boundary to respect when the user
 * actually began using the system. Otherwise defaults to 12 months back as a reasonable
 * fallback to prevent overwhelming UI with too much historical data.
 *
 * @param contractDate - Optional contract start date (when user began using system)
 * @param endDate - The end boundary (typically today or data cutoff date)
 * @returns Date range from contract start (or 12 months back) to end date
 *
 * @example
 * // With contract date
 * getAllTimeRange('2023-06-01', new Date('2025-01-15'))
 * // Returns: { fromDate: '2023-06-01', toDate: '2025-01-15' }
 *
 * // Without contract date
 * getAllTimeRange(null, new Date('2025-01-15'))
 * // Returns: { fromDate: '2024-01-01', toDate: '2025-01-15' }
 */
function getAllTimeRange(
  contractDate: string | undefined,
  endDate: Date,
): IDateRange {
  if (contractDate) {
    return {
      fromDate: format(new Date(contractDate), ISO_DATE_FORMAT),
      toDate: format(endDate, ISO_DATE_FORMAT),
    };
  }

  return {
    fromDate: format(startOfMonth(subMonths(endDate, 12)), ISO_DATE_FORMAT),
    toDate: format(endDate, ISO_DATE_FORMAT),
  };
}

/**
 * Ensures the date range doesn't precede the contract start date.
 *
 * This prevents showing data from before the user actually began using the system,
 * which would be empty/misleading. Takes the later of the calculated fromDate or
 * the contract start date as the effective start boundary.
 *
 * @param dateRange - The initially calculated date range
 * @param contractDate - Contract start date to use as minimum boundary
 * @returns Adjusted date range with fromDate clamped to contract start date
 *
 * @example
 * // Range wants Jan 2024, but contract started Mar 2024
 * applyContractDateConstraint(
 *   { fromDate: '2024-01-01', toDate: '2024-06-30' },
 *   new Date('2024-03-01')
 * )
 * // Returns: { fromDate: '2024-03-01', toDate: '2024-06-30' }
 */
function applyContractDateConstraint(
  dateRange: IDateRange,
  contractDate?: string,
): IDateRange {
  if (!contractDate) {
    return dateRange;
  }

  const fromDate = format(
    max([new Date(dateRange.fromDate), new Date(contractDate)]),
    ISO_DATE_FORMAT,
  );

  return { fromDate, toDate: dateRange.toDate };
}

type GetDateFilterWindowParams = {
  monthOffset: number;
  contractDate?: string;
  isQuarterFilter?: boolean;
  filterAvailableTill?: string;
};

/**
 * Calculates the appropriate date range for dashboard filtering based on user selection.
 *
 * This is the main entry point for converting filter UI selections (like "Last 3 Months",
 * "Last 2 Quarters", "All Time") into concrete date ranges for API queries. Handles
 * special cases like contract date boundaries and quarter vs month-based calculations.
 *
 * @param params - Configuration object for date range calculation
 * @param params.monthOffset - Filter selection value (number of months/quarters back, or special values)
 * @param params.contractDate - Optional contract start date to enforce as minimum boundary
 * @param params.isQuarterFilter - Whether to calculate quarters instead of months
 * @param params.filterAvailableTill - Optional data cutoff date (defaults to today)
 * @returns Concrete date range ready for API consumption
 *
 * @example
 * // Last 3 months filter
 * getDateFilterWindow({ monthOffset: 3 })
 * // Returns: { fromDate: '2024-10-01', toDate: '2024-12-31' }
 *
 * // Last 2 quarters with contract constraint
 * getDateFilterWindow({
 *   monthOffset: 6,
 *   isQuarterFilter: true,
 *   contractDate: '2024-08-01'
 * })
 * // Returns date range clamped to contract start
 */
export const getDateFilterWindow = ({
  monthOffset,
  contractDate,
  isQuarterFilter,
  filterAvailableTill,
}: GetDateFilterWindowParams): IDateRange => {
  const endDate = filterAvailableTill
    ? new Date(filterAvailableTill)
    : new Date();

  // Handle ALL_TIME filter separately - has different logic
  if (monthOffset === SpecialDateFilterValue.ALL_TIME) {
    return getAllTimeRange(contractDate || undefined, endDate);
  }

  // Calculate date range based on filter type (quarter vs month)
  const dateRange = isQuarterFilter
    ? getQuarterDateRange(monthsToQuarters(monthOffset), endDate)
    : getMonthDateRange(monthOffset, endDate);

  // Apply contract date constraint to prevent showing pre-contract data
  return applyContractDateConstraint(dateRange, contractDate);
};

export const getCurrentDateLocale = () =>
  DateLocales[
    (import.meta.env.VITE_DEFAULT_LANG as keyof typeof DateLocales) || 'en'
  ];

export const formatDateToDisplayLabel = (date: string | Date) => {
  return format(
    typeof date === 'string' ? parseISO(date) : date,
    'MMM dd, yyyy',
  );
};
